import { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell,
} from "recharts";
import { ArrowUpRight, ArrowDownRight, TrendUp, ArrowDown, ArrowUp } from "@phosphor-icons/react";

const SHIP_COLORS = ["#0A0A0A", "#0020C2", "#F97316", "#A1A1AA"];

export default function Dashboard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  if (!data) return <div className="label-mini">Loading…</div>;

  const revChange = data.revenue_last_month
    ? ((data.revenue_month - data.revenue_last_month) / data.revenue_last_month) * 100
    : 0;

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-mini">Operations Console</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">Dashboard</h1>
        </div>
        <div className="text-xs text-zinc-500 tracking-tight">
          Snapshot · {new Date().toLocaleString()}
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
        {[
          { label: "Revenue · This Month", value: fmtMoney(data.revenue_month), sub: `${revChange >= 0 ? "▲" : "▼"} ${Math.abs(revChange).toFixed(1)}% vs last`, icon: TrendUp, positive: revChange >= 0 },
          { label: "Profit · This Month", value: fmtMoney(data.profit_month), sub: `After ${fmtMoney(data.cost_month)} cost`, icon: TrendUp, positive: (data.profit_month || 0) >= 0 },
          { label: "Outstanding Receivables", value: fmtMoney(data.receivables), sub: data.customer_credit ? `Customers owe us · ${fmtMoney(data.customer_credit)} in credit` : "Customers owe us", icon: ArrowDown },
          { label: "Outstanding Payables", value: fmtMoney(data.payables), sub: data.supplier_advances ? `We owe suppliers · ${fmtMoney(data.supplier_advances)} advanced` : "We owe suppliers", icon: ArrowUp },
        ].map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} className="bg-white p-5" data-testid={`kpi-${k.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              <div className="flex items-start justify-between">
                <div className="label-mini">{k.label}</div>
                <Icon size={14} className="text-zinc-400" />
              </div>
              <div className="kpi-num text-2xl sm:text-3xl mt-3 break-all">{k.value}</div>
              <div className={`text-[11px] mt-2 tracking-tight ${k.positive === false ? "text-red-700" : k.positive ? "text-emerald-700" : "text-zinc-500"}`}>
                {k.sub}
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue trend */}
        <div className="card-sharp lg:col-span-2 p-5" data-testid="revenue-chart-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-mini">6-Month Trend</div>
              <h3 className="text-lg font-semibold tracking-tight">Revenue (PKR)</h3>
            </div>
          </div>
          <div className="h-64 w-full" style={{ minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
              <BarChart data={data.revenue_trend || []} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                <CartesianGrid stroke="#E4E4E7" vertical={false} />
                <XAxis dataKey="month" stroke="#52525B" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#52525B" fontSize={11} tickLine={false} axisLine={false}
                       tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                <Tooltip
                  contentStyle={{ background: "#0A0A0A", border: "none", borderRadius: 2, color: "white", fontSize: 12 }}
                  cursor={{ fill: "#F4F4F5" }}
                  formatter={(v) => [fmtMoney(v), "Revenue"]}
                />
                <Bar dataKey="revenue" fill="#0A0A0A" radius={0} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pipeline */}
        <div className="card-sharp p-5" data-testid="pipeline-card">
          <div className="label-mini">Pipeline</div>
          <h3 className="text-lg font-semibold tracking-tight mb-3">Orders by Status</h3>
          <div className="space-y-2">
            {Object.entries(data.status_counts || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between border-b border-zinc-100 pb-2 last:border-0">
                <StatusBadge status={k} />
                <span className="font-mono text-sm font-semibold tnum">{v}</span>
              </div>
            ))}
            {Object.keys(data.status_counts || {}).length === 0 && (
              <div className="text-xs text-zinc-500">No orders yet.</div>
            )}
          </div>
        </div>

        {/* Top customers */}
        <div className="card-sharp lg:col-span-2 p-5" data-testid="top-customers-card">
          <div className="label-mini">Revenue Leaders</div>
          <h3 className="text-lg font-semibold tracking-tight mb-3">Top Customers</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left label-mini border-b border-zinc-200">
                <th className="py-2">Customer</th>
                <th className="py-2">Code</th>
                <th className="py-2 text-right">Orders</th>
                <th className="py-2 text-right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {(data.top_customers || []).map((c) => (
                <tr key={c.code} className="border-b border-zinc-100 last:border-0">
                  <td className="py-2 font-medium">{c.name}</td>
                  <td className="py-2 font-mono text-xs text-zinc-500">{c.code}</td>
                  <td className="py-2 text-right font-mono tnum">{c.orders}</td>
                  <td className="py-2 text-right font-mono tnum">{fmtMoney(c.revenue)}</td>
                </tr>
              ))}
              {(data.top_customers || []).length === 0 && (
                <tr><td colSpan={4} className="py-4 text-center text-xs text-zinc-500">No customer revenue yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Shipping */}
        <div className="card-sharp p-5" data-testid="shipping-card">
          <div className="label-mini">Logistics Mix</div>
          <h3 className="text-lg font-semibold tracking-tight mb-3">Shipping Method</h3>
          <div className="h-48 w-full" style={{ minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={150}>
              <PieChart>
                <Pie
                  data={data.shipping_distribution || []}
                  dataKey="count"
                  nameKey="method"
                  outerRadius={70}
                  innerRadius={36}
                  paddingAngle={2}
                >
                  {(data.shipping_distribution || []).map((_, i) => (
                    <Cell key={i} fill={SHIP_COLORS[i % SHIP_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "none", color: "white", fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1 mt-3">
            {(data.shipping_distribution || []).map((d, i) => (
              <div key={d.method} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2" style={{ background: SHIP_COLORS[i % SHIP_COLORS.length] }} />
                  <span className="capitalize">{(d.method || "").replace("_", " ")}</span>
                </div>
                <span className="font-mono tnum">{d.count}</span>
              </div>
            ))}
            {(data.shipping_distribution || []).length === 0 && (
              <div className="text-xs text-zinc-500">No data yet.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
